/**
 * Feb-2026 (User-Wunsch 27.05.2026) — Disposition-Übersicht für Händler.
 *
 * Universelles Such-Feld, das per `/api/dealer/disposition/search?q=...`
 * ALLE relevanten Felder durchsucht (kein Feld unberücksichtigt):
 *   * Fahrzeug-Daten: Marke, Modell, Titel, Hersteller, Typ, Farbe, VIN,
 *     Kennzeichen, HSN/TSN, interne Nr., Stand-Platz, Schlüssel-Nr., Leitzahl,
 *     Emission/Kraftstoff/Getriebe/Antrieb, Beschreibung/Bemerkungen
 *   * Standort: Ort, Straße, PLZ, Standort-Code
 *   * Numerisch (bei Zahleneingabe): Preis, B2B-Preis, KM, Baujahr, Vorbesitzer,
 *     Provision, Hubraum, Leistung, Bestand
 *   * Kunden-/Käuferdaten: dispo_customer + reservations (Name, Firma, E-Mail,
 *     Handy, PLZ, Stadt) sowie Verkäufer & Provisionsabrechnung
 *   * Transport: Tracking-Nummer, Anbieter, Status
 *
 * Pro Treffer wird neben den Fahrzeug-Eckdaten der aktuelle Status
 * (verfügbar / reserviert / verkauft + Transport-Status) gezeigt, plus
 * Direkt-Link zur Disposition-Sektion des Inserats.
 *
 * Aufruf: ``/dealer/disposition`` (Header-Dropdown).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Loader2, Truck, X, AlertTriangle, ClipboardCheck, Layers, History, ScanLine, SlidersHorizontal, Check, Clock, Info, MapPin, Building2, Globe, Plus, Factory, ChevronDown, FileCheck2, User, MoreHorizontal, PackageSearch, Trash2, Settings } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import api, { setApiOrgBranch } from "../lib/api";
import { useAuth } from "../lib/auth";
import EmployeeContextBanner from "../components/vermittler/EmployeeContextBanner";
import OrgBranchSwitcher from "../components/org/OrgBranchSwitcher";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { DISPO_GUIDES } from "../lib/dispoGuides";
import LadenhueterActionModal from "../components/listing/dispo/LadenhueterActionModal";
import DispositionVehicleCard from "../components/dealer/DispositionVehicleCard";
import B2BForeignActionModal from "../components/dealer/B2BForeignActionModal";
import OfferSendModal from "../components/dealer/OfferSendModal";
import TestDriveModal from "../components/dealer/TestDriveModal";
import SofortmitnahmeModal from "../components/dealer/SofortmitnahmeModal";
import { DeliveryOrderModal, TransportOrderModal } from "../components/disposition/DispoActionModals";
import RedPlateExpiryAlert from "../components/dealer/RedPlateExpiryAlert";
import VehicleIntakeQrCard from "../components/disposition/VehicleIntakeQrCard";
import DispoSettingsModal from "../components/disposition/DispoSettingsModal";
import DispoBulkEditModal from "../components/dealer/DispoBulkEditModal";
import DispoBulkHistoryModal from "../components/dealer/DispoBulkHistoryModal";
import PapersBatchScanModal from "../components/dealer/PapersBatchScanModal";
import { ManufacturerCatalogImport, CatalogOrderCodePicker } from "../components/dealer/ManufacturerCatalog";
import HsnTsnLookupModal from "../components/dealer/HsnTsnLookupModal";
import LocationCleanupModal from "../components/dealer/LocationCleanupModal";
import ListingDupesModal from "../components/dealer/ListingDupesModal";

// Jun-2026 — Einheitliche Filter-Button-Stile: inaktiv = semantische Farbe,
// aktiv = schwarz (#111827) + gelbes Häkchen. Schwarz erscheint also NUR,
// wenn der jeweilige Filter aktiviert ist.
const FILTER_BASE = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[11px] font-bold uppercase tracking-wider transition-colors";
const FILTER_ACTIVE = "bg-orange-500 text-[#111827] border-orange-500 shadow-md font-black tw-clear-filters-attn";
// Jul-2026 (User-Wunsch) — inaktive Filter EINHEITLICH weiß + harmonisierte Schrift.
const FILTER_IDLE = "bg-white text-gray-600 border-gray-300 hover:border-[#111827]";

export default function DispositionPage() {
    const { user } = useAuth();
    const [orgBranch, setOrgBranch] = useState(null);
    const [branchLabel, setBranchLabel] = useState("");
    useEffect(() => {
        setApiOrgBranch(orgBranch);
        return () => setApiOrgBranch(null);
    }, [orgBranch]);
    const [q, setQ] = useState("");
    const [debounced, setDebounced] = useState("");
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    // Phase 4f.B (Feb 2026) — Multi-Standort-Filter
    const [sites, setSites] = useState([]);
    const [siteFilter, setSiteFilter] = useState("all");
    // Fällig-/Überfällig-Ampel: Anzahl überfälliger offener Zulassungen (rotes Badge).
    const [overdueReg, setOverdueReg] = useState(0);
    useEffect(() => {
        api.get("/dealer/disposition/open-registrations")
            .then((r) => setOverdueReg(r.data?.overdue || 0))
            .catch(() => setOverdueReg(0));
    }, []);
    // Jun-2026 (User-Wunsch) — Übergeordneter Geltungsbereich der Dispo-Liste:
    //   my_site     → nur Fahrzeuge am eingeloggten Standort
    //   all_sites   → alle eigenen Fahrzeuge (alle verknüpften Standorte)
    //   all_dealers → zusätzlich fremde, B2B-sichtbare Fahrzeuge anderer Händler
    const [scope, setScope] = useState("all_dealers");
    const [noSite, setNoSite] = useState(false);
    const [showScopeHelp, setShowScopeHelp] = useState(false);
    // Aug-2026 (User-Wunsch): Zahnrad → DMS-Zugangsdaten
    const [dispoSettingsOpen, setDispoSettingsOpen] = useState(false);
    // Jun-2026 (User-Wunsch) — Marken-/Umkreis-Filter (v. a. für „Alle Händler").
    const [brandFilter, setBrandFilter] = useState("");
    // Jul-2026 (User-Wunsch) — Farb-Filter (Basisfarbe, Herstellerfarbe, Farbangabe im Titel).
    const [colorFilter, setColorFilter] = useState("");
    // Jul-2026 (User-Wunsch) — Farb-Schnellchips: häufigste Bestandsfarben als Ein-Klick-Filter.
    const [colorChips, setColorChips] = useState([]);
    const [radiusKm, setRadiusKm] = useState("");
    const [dealerFilter, setDealerFilter] = useState(""); // Jul-2026: bestimmten (opt-in) Händler auswählen
    const [locationFilter, setLocationFilter] = useState(""); // Jul-2026: Fahrzeug-Standort (Freitext location)
    const [standortQuery, setStandortQuery] = useState(""); // Jul-2026: gemeinsames Standort-Feld (Filiale ODER Fahrzeug-Standort)
    const [onlyB2c, setOnlyB2c] = useState(false); // Jul-2026: NUR PRIVATE — ausschließlich B2C-Inserate
    const [sharedDealers, setSharedDealers] = useState([]);
    // Jun-2026 (User-Wunsch) — Direkt-Aktionen für fremde Fahrzeuge.
    const [foreignReserveItem, setForeignReserveItem] = useState(null);
    const [foreignDealItem, setForeignDealItem] = useState(null);
    // Feb/Jun-2026 (User-Wunsch) — Status-Filter, jetzt MEHRFACHAUSWAHL.
    const [statusFilters, setStatusFilters] = useState([]); // [] = Alle
    // Jun-2026 (User-Wunsch) — Mehrfach-Zustandsfilter (NW/TZ/VFW/GW)
    const [conditionFilters, setConditionFilters] = useState([]);
    // Jun-2026 (User-Wunsch) — Lager-Status-Filter „Bestand" / „Vorlauf", MEHRFACH.
    const [stockFilters, setStockFilters] = useState([]); // [] = Alle
    // Jun-2026 (User-Wunsch) — Universelle „Von–Bis"-Bereichsfilter
    const [showRanges, setShowRanges] = useState(false);
    const [ranges, setRanges] = useState({
        min_year: "", max_year: "", min_mileage: "", max_mileage: "", min_price: "", max_price: "",
    });
    const setRange = useCallback((k, v) => setRanges((p) => ({ ...p, [k]: v })), []);
    const clearRanges = useCallback(() => setRanges({ min_year: "", max_year: "", min_mileage: "", max_mileage: "", min_price: "", max_price: "" }), []);
    const activeRangeCount = Object.values(ranges).filter((v) => String(v).trim() !== "").length;
    // Juni-2026 (User-Wunsch) — Ladenhüter-Filter (Standtage > 120T), per
    // Klick auf die Kennzahl-Kachel „Ladenhüter >120T".
    const [ladenhueterFilter, setLadenhueterFilter] = useState(false);
    // Jun-2026 (User-Wunsch) — Liefertermin: Filter „nur Bestellfahrzeuge mit
    // Liefertermin" + Sortierung „nach Liefertermin (früheste zuerst)".
    const [deliveryFilter, setDeliveryFilter] = useState(false);
    const [deliverySort, setDeliverySort] = useState(false);
    // Jun-2026 (User-Wunsch) — Schnellfilter „Reservierung läuft bald ab (<24h)".
    const [expiringFilter, setExpiringFilter] = useState(false);
    // Juni-2026 (User-Wunsch) — Schnellaktion „Preis senken / boosten" (Ladenhüter)
    const [lhAction, setLhAction] = useState(null);
    // Jun-2026 (User-Wunsch) — „Angebot erstellen" (PDF/E-Mail/WhatsApp) je Fahrzeug
    const [offerItem, setOfferItem] = useState(null);
    const [testDriveItem, setTestDriveItem] = useState(null);
    const [sofortmitnahmeItem, setSofortmitnahmeItem] = useState(null);
    // Jun-2026 (User-Wunsch) — interner Auslieferungsauftrag + Transportauftrag je Fahrzeug
    const [deliveryItem, setDeliveryItem] = useState(null);
    const [transportItem, setTransportItem] = useState(null);
    // Feb-2026 (User-Wunsch) — Kennzahl-Leiste (Lagerzustand auf einen Blick)
    const [stats, setStats] = useState(null);
    // Jul-2026 (User-Wunsch) — „Weitere Funktionen"-Menü steuert den Katalog-Import-Dialog.
    const [showCatalogImport, setShowCatalogImport] = useState(false);
    // Juni-2026 (User-Wunsch) — Gruppenbearbeitung (Bulk Edit): Mehrfachauswahl
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [showBulk, setShowBulk] = useState(false);
    // Juni-2026 (User-Wunsch) — weitere Dispo-Werkzeuge
    const [showHistory, setShowHistory] = useState(false);
    const [showCatalog, setShowCatalog] = useState(false); // Hersteller-Katalog-Liste (Jun 2026)
    const [showPapersBatch, setShowPapersBatch] = useState(false);
    const [showHsnTsn, setShowHsnTsn] = useState(false); // HSN/TSN nachschlagen (Jun 2026)
    const [showLocationCleanup, setShowLocationCleanup] = useState(false); // Standorte bereinigen (Jul 2026)
    const [showListingDupes, setShowListingDupes] = useState(false); // FIN-Doubletten (Jul 2026)
    // Jun-2026 (User-Wunsch) — Paginierung (Seitengröße 10/20/50).
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);
    const [searchParams, setSearchParams] = useSearchParams();

    // Header-Dropdown-Deeplink: ?action=papers-batch-scan öffnet den Sammel-Scan
    useEffect(() => {
        const action = searchParams.get("action");
        if (action === "papers-batch-scan") {
            setShowPapersBatch(true);
            const next = new URLSearchParams(searchParams);
            next.delete("action");
            setSearchParams(next, { replace: true });
        }
        // Jul-2026 (Verkaufs-Cockpit) — ?q= befüllt die Universal-Suche direkt.
        const qp = searchParams.get("q");
        if (qp) {
            setQ(qp);
            const next = new URLSearchParams(searchParams);
            next.delete("q");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    // Jun-2026 (User-Wunsch) — Status mehrfach an-/abwählbar; „Alle" = leere Auswahl.
    const toggleStatus = useCallback((key) => {
        setLadenhueterFilter(false);
        setExpiringFilter(false);
        if (key === "all") { setStatusFilters([]); return; }
        setStatusFilters((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]);
    }, []);
    const applyStatus = useCallback((key) => {
        setLadenhueterFilter(false);
        setExpiringFilter(false);
        setStatusFilters(key === "all" ? [] : [key]);
    }, []);
    const applyLadenhueter = useCallback(() => {
        setStatusFilters([]);
        setExpiringFilter(false);
        setLadenhueterFilter((v) => !v);
    }, []);
    // Jun-2026 (User-Wunsch) — Schnellfilter „Reservierung läuft bald ab (<24h)".
    const applyExpiring = useCallback(() => {
        setLadenhueterFilter(false);
        setStatusFilters([]);
        setExpiringFilter((v) => !v);
    }, []);
    // Jun-2026 (User-Wunsch) — Zustands-Filter mehrfach an-/abwählbar.
    const toggleCondition = useCallback((code) => {
        setConditionFilters((p) => p.includes(code) ? p.filter((x) => x !== code) : [...p, code]);
    }, []);
    // Jun-2026 (User-Wunsch) — Lager-Status mehrfach an-/abwählbar.
    const toggleStock = useCallback((key) => {
        setStockFilters((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]);
    }, []);
    const clearAllFilters = useCallback(() => {
        setStatusFilters([]); setConditionFilters([]); setStockFilters([]);
        setLadenhueterFilter(false); setExpiringFilter(false); setSiteFilter("all"); setQ("");
        setDeliveryFilter(false); setDeliverySort(false);
        setScope("all_dealers"); setBrandFilter(""); setColorFilter(""); setRadiusKm(""); setOnlyB2c(false);
        clearRanges();
    }, [clearRanges]);

    // Jul-2026 (User-Wunsch) — „Gruppenbearbeitung" nur für Inhaber/Admin sowie
    // Mitarbeiter mit Modul Disposition / Gebiets-, Bereichsleitung / Geschäfts-
    // führung (Bereichsfreigabe). Andere Mitarbeiter sehen den Button nicht.
    const canBulkEdit = (() => {
        if (!user) return false;
        if (user.role === "b2b" || user.role === "admin") return true;
        const mods = Array.isArray(user.resolved_modules) ? user.resolved_modules : [];
        return mods.some((m) => ["disposition", "gebietsleitung", "bereichsleitung", "geschaeftsfuehrung"].includes(m));
    })();

    // Jun-2026 (User-Wunsch) — alle Filter in EINEM einklappbaren Bereich;
    // `anyFilterActive` steuert „Alle Filter löschen", `activeFilterCount` das Badge.
    const anyFilterActive = statusFilters.length > 0 || conditionFilters.length > 0
        || stockFilters.length > 0 || activeRangeCount > 0 || ladenhueterFilter
        || expiringFilter || (siteFilter !== "all") || q.trim() !== ""
        || deliveryFilter || deliverySort || (scope !== "all_dealers")
        || brandFilter.trim() !== "" || colorFilter.trim() !== "" || String(radiusKm).trim() !== "" || onlyB2c;
    const activeFilterCount = statusFilters.length + conditionFilters.length + stockFilters.length
        + activeRangeCount + (ladenhueterFilter ? 1 : 0) + (expiringFilter ? 1 : 0)
        + (siteFilter !== "all" ? 1 : 0) + (deliveryFilter ? 1 : 0) + (deliverySort ? 1 : 0)
        + (scope !== "all_dealers" ? 1 : 0) + (brandFilter.trim() ? 1 : 0) + (colorFilter.trim() ? 1 : 0) + (String(radiusKm).trim() ? 1 : 0) + (onlyB2c ? 1 : 0);
    // Jun-2026 (User-Wunsch) — Filter & „Von–Bis"-Bereiche starten IMMER eingeklappt.
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const p = new URLSearchParams();
        p.set("scope", scope);
        if (scope === "all_sites" && siteFilter && siteFilter !== "all") p.set("site_code", siteFilter);
        if (scope === "all_dealers" && dealerFilter) p.set("dealer_id", dealerFilter);
        if (onlyB2c) p.set("only_b2c", "true");
        api.get(`/dealer/disposition/stats?${p.toString()}`)
            .then((r) => { if (!cancelled) setStats(r.data); })
            .catch(() => { if (!cancelled) setStats(null); });
        return () => { cancelled = true; };
    }, [scope, siteFilter, dealerFilter, onlyB2c]);

    // Debounce: 300ms nach letztem Tastendruck
    useEffect(() => {
        const t = setTimeout(() => setDebounced(q), 300);
        return () => clearTimeout(t);
    }, [q]);

    // Lade Standorte einmalig
    useEffect(() => {
        let alive = true;
        api.get("/users/me/dealer-sites")
            .then((r) => { if (alive) setSites(r.data?.items || []); })
            .catch(() => { if (alive) setSites([]); });
        return () => { alive = false; };
    }, []);

    const loadReqIdRef = useRef(0);
    const load = useCallback(async () => {
        // Jul-2026 (Bugfix, Testing) — Request-Guard: langsame, veraltete Antworten
        // (z. B. all_dealers) dürfen den aktuellen Filterstand nicht überschreiben.
        const reqId = ++loadReqIdRef.current;
        setLoading(true);
        try {
            const params = new URLSearchParams({ q: debounced, limit: "100" });
            params.set("scope", scope);
            if (scope === "all_sites" && siteFilter && siteFilter !== "all") params.set("site_code", siteFilter);
            if (brandFilter.trim()) params.set("brand", brandFilter.trim());
            if (colorFilter.trim()) params.set("color", colorFilter.trim());
            if (scope === "all_dealers" && String(radiusKm).trim()) params.set("radius_km", String(radiusKm).trim());
            if (scope === "all_dealers" && dealerFilter) params.set("dealer_id", dealerFilter);
            if (onlyB2c) params.set("only_b2c", "true");
            if (statusFilters.length) params.set("status", statusFilters.join(","));
            if (conditionFilters.length) params.set("conditions", conditionFilters.join(","));
            if (stockFilters.length) params.set("stock_state", stockFilters.join(","));
            for (const [k, v] of Object.entries(ranges)) {
                if (String(v).trim() !== "") params.set(k, String(v).trim());
            }
            if (ladenhueterFilter) params.set("ladenhueter", "true");
            if (expiringFilter) params.set("reservation_expiring", "true");
            if (deliveryFilter) params.set("has_delivery", "true");
            if (deliverySort) params.set("sort", "delivery");
            const r = await api.get(`/dealer/disposition/search?${params.toString()}`);
            if (reqId !== loadReqIdRef.current) return;
            setItems(r.data?.items || []);
            setPage(1);
            setNoSite(!!r.data?.no_site);
        } catch {
            if (reqId !== loadReqIdRef.current) return;
            setItems([]);
            setNoSite(false);
        } finally {
            if (reqId === loadReqIdRef.current) setLoading(false);
        }
    }, [debounced, scope, siteFilter, statusFilters, conditionFilters, stockFilters, ranges, ladenhueterFilter, expiringFilter, deliveryFilter, deliverySort, brandFilter, colorFilter, radiusKm, dealerFilter, onlyB2c, orgBranch]);

    useEffect(() => { load(); }, [load]);

    // Jul-2026 (User-Wunsch) — Chips aus den geladenen Fahrzeugen berechnen;
    // während aktiver Farb-Filterung eingefroren (sonst verschwänden alle Chips).
    useEffect(() => {
        if (colorFilter.trim()) return;
        const counts = {};
        for (const it of items || []) {
            const raw = (it.color_base || "").trim() || (it.color || "").trim();
            if (!raw || raw.length > 16) continue;
            const key = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
            counts[key] = (counts[key] || 0) + 1;
        }
        setColorChips(Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8));
    }, [items, colorFilter]);

    // Jul-2026 (User-Wunsch) — Händler-Auswahl: Liste der freigegebenen (opt-in) Händler laden.
    useEffect(() => {
        api.get("/dealer/shared-inventory-dealers")
            .then((r) => setSharedDealers(r.data?.items || []))
            .catch(() => setSharedDealers([]));
    }, [orgBranch]);

    // Jul-2026 — Seite/Standort-Filter zurücksetzen, wenn sich der Standort-Filter
    // oder der Scope ändert (Standort-Filter nur im „Alle Händler"-Scope sinnvoll).
    useEffect(() => { setPage(1); }, [locationFilter]);
    useEffect(() => { if (scope !== "all_dealers") { setLocationFilter(""); setStandortQuery(""); } }, [scope]);

    // Jul-2026 (User-Wunsch, Variante b) — EIN gemeinsames „Standort"-Feld:
    // Eingabe wird aufgelöst → passt sie auf eine Händler-Filiale, wird nach
    // Händler (Backend) gefiltert; sonst als physischer Fahrzeug-Standort (`location`).
    const dealerLabelOf = (d) => [d.street, d.name].filter(Boolean).join(" · ");
    // Jul-2026 (Bugfix, User-Meldung) — Standort-Normalisierung für Dubletten-Erkennung.
    const normStandort = (s) => (s || "")
        .toLowerCase()
        .replace(/straße|strasse|str\.(?=\s|$)|str(?=\s|$)/g, "str")
        .replace(/[·,]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    useEffect(() => {
        const val = standortQuery.trim();
        if (!val) { setDealerFilter(""); setLocationFilter(""); return; }
        const dealer = sharedDealers.find((d) => dealerLabelOf(d) === val);
        if (dealer) { setDealerFilter(dealer.id); setLocationFilter(""); setScope("all_dealers"); }
        else { setDealerFilter(""); setLocationFilter(val); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [standortQuery]);

    const brandOptions = [...new Set((items || []).map((i) => i.brand).filter(Boolean))].sort();
    // Jul-2026 (User-Wunsch) — Filter „Fahrzeug-Standort" (Freitext `location`,
    // z. B. König-Import). Optionen aus den aktuellen Treffern; clientseitig
    // gefiltert (Liste liegt im „Alle Händler"-Scope vollständig vor).
    const locationOptions = [...new Set((items || []).map((i) => i.location).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, "de"));
    // Jul-2026 (Variante b) — gemeinsame Standort-Vorschläge: Händler-Filialen + Fahrzeug-Standorte,
    // alphabetisch nach ORT sortiert (Fahrzeug-Standort: erstes Wort = Ort; Filiale: d.city).
    const _dealerOpts = sharedDealers.map((d) => ({ label: dealerLabelOf(d), city: d.city || "", key: normStandort(`${d.city || ""} ${d.street || ""}`), isDealer: true })).filter((o) => o.label);
    const _locOpts = locationOptions.map((l) => ({ label: l, city: (l.split(" ")[0] || ""), key: normStandort(l), isDealer: false }));
    // Jul-2026 (Bugfix, User-Meldung) — Dubletten gleicher Standorte zusammenführen:
    // 1) Schreibweisen normalisieren (Straße/Str., Groß/Klein) → ein Eintrag pro Standort,
    //    Filial-Einträge (mit Händlername) gewinnen gegen Freitext-Fahrzeug-Standorte.
    // 2) Einträge OHNE Hausnummer entfernen, wenn derselbe Standort MIT Hausnummer existiert.
    const _byKey = new Map();
    for (const o of [..._locOpts, ..._dealerOpts]) {
        if (!o.key) continue;
        const prev = _byKey.get(o.key);
        if (!prev || (o.isDealer && !prev.isDealer)) _byKey.set(o.key, o);
    }
    const _deduped = [..._byKey.values()].filter((o) =>
        ![..._byKey.keys()].some((k) => k !== o.key && k.startsWith(`${o.key} `) && /\d/.test(k.slice(o.key.length))));
    const standortOptions = _deduped
        .sort((a, b) => (a.city || "").localeCompare(b.city || "", "de") || a.label.localeCompare(b.label, "de"))
        .map((o) => o.label);
    const visibleItems = items && locationFilter.trim()
        ? items.filter((i) => (i.location || "").toLowerCase().includes(locationFilter.trim().toLowerCase()))
        : items;

    // Jun-2026 (User-Wunsch) — Paginierung der Ergebnisliste (Seitengröße 10/20/50).
    const totalItems = visibleItems?.length || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const curPage = Math.min(page, totalPages);
    const pagedItems = visibleItems ? visibleItems.slice((curPage - 1) * pageSize, curPage * pageSize) : visibleItems;

    // Aktueller Standort für den QR-Druck (User-Wunsch): bevorzugt der oben gewählte
    // OrgBranch-Standort, sonst der gefilterte eigene Standort, sonst der eigene Sitz.
    const currentStandort = (() => {
        if (branchLabel) return branchLabel;
        if (scope === "all_sites" && siteFilter && siteFilter !== "all") {
            const s = sites.find((x) => x.code === siteFilter);
            if (s) return [s.code, s.name].filter(Boolean).join(" · ");
        }
        const s0 = sites[0];
        if (s0) return [s0.code, s0.name].filter(Boolean).join(" · ");
        return user?.company_name || "";
    })();


    return (
        <div className="min-h-screen bg-gray-50">
            <EmployeeContextBanner />
            {/* Header band — dunkles Design konsistent mit Rechnungen/Kunden & Warenwirtschaft */}
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dashboard" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-dashboard">
                        ← Dashboard
                    </Link>
                    <div className="flex items-start justify-between gap-6 mt-4">
                        <div className="flex-1 min-w-0">
                            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3" data-testid="disposition-page-title">
                                <Truck className="w-9 h-9 text-[#FFCC00]" /> Disposition
                                {/* Aug-2026 (User-Wunsch): Zahnrad mit echter Funktion — DMS-Zugangsdaten */}
                                <button type="button" onClick={() => setDispoSettingsOpen(true)}
                                    title="DMS-Zugangsdaten (e-Archivsystem) hinterlegen"
                                    className="text-gray-400 hover:text-[#FFCC00] transition-colors"
                                    data-testid="dispo-settings-gear">
                                    <Settings className="w-5 h-5" />
                                </button>
                            </h1>
                            {/* Aug-2026 (User-Wunsch): Zahnrad (ohne echte Funktion) + Beschreibungstext entfernt. */}
                            <div className="flex items-center gap-2 flex-wrap mt-3">
                                <OrgBranchSwitcher user={user} value={orgBranch} onChange={setOrgBranch} onLabelChange={setBranchLabel} />
                                {/* Jul-2026 (User-Wunsch) — seltener genutzte Funktionen unter „Weitere Funktionen" gebündelt */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button type="button" variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white text-[#111827] hover:bg-gray-100" data-testid="disposition-more-functions-btn">
                                            <MoreHorizontal className="w-4 h-4 mr-1.5" /> Weitere Funktionen <ChevronDown className="w-3.5 h-3.5 ml-1" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="w-64">
                                        <DropdownMenuItem onClick={() => setShowPapersBatch(true)} data-testid="more-fn-papers-batch">
                                            <ScanLine className="w-4 h-4 mr-2" /> Sammel-Scan Papiere
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setShowLocationCleanup(true)} data-testid="more-fn-location-cleanup">
                                            <MapPin className="w-4 h-4 mr-2" /> Standorte bereinigen
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setShowListingDupes(true)} data-testid="more-fn-listing-dupes">
                                            <Layers className="w-4 h-4 mr-2" /> FIN-Doubletten zusammenführen
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setShowHistory(true)} data-testid="more-fn-bulk-history">
                                            <History className="w-4 h-4 mr-2" /> Verlauf Gruppenbearbeitung
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild data-testid="more-fn-vehicle-inventory">
                                            <Link to="/dealer/fahrzeuginventur"><ClipboardCheck className="w-4 h-4 mr-2" /> Fahrzeuginventur</Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setShowCatalogImport(true)} data-testid="more-fn-catalog-import">
                                            <PackageSearch className="w-4 h-4 mr-2" /> Bestellcodes importieren
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <ManufacturerCatalogImport onImported={() => load()} hideTrigger externalOpen={showCatalogImport} onExternalOpenChange={setShowCatalogImport} />
                                <Button type="button" onClick={() => setShowHsnTsn(true)} variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white text-[#111827] hover:bg-gray-100" data-testid="open-hsn-tsn-lookup-btn">
                                    <Search className="w-4 h-4 mr-1.5" /> HSN / TSN nachschlagen
                                </Button>
                                <Button asChild variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white text-[#111827] hover:bg-gray-100" data-testid="open-create-vehicle-btn">
                                    <Link to="/create-listing"><Plus className="w-4 h-4 mr-1.5" /> Fahrzeug anlegen</Link>
                                </Button>
                                <Button asChild className="h-9 rounded-sm bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] text-sm font-bold" data-testid="open-order-vehicle-btn">
                                    <Link to="/create-listing?mode=bestellung"><Plus className="w-4 h-4 mr-1.5" /> Bestellung anlegen</Link>
                                </Button>
                                <Button asChild variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white text-[#111827] hover:bg-gray-100" data-testid="open-transport-planning-btn">
                                    <Link to="/dealer/transport"><Truck className="w-4 h-4 mr-1.5" /> Transportplanung</Link>
                                </Button>
                                <Button asChild variant="outline" className="relative h-9 rounded-sm text-sm font-bold bg-white text-[#111827] hover:bg-gray-100" data-testid="open-offene-zulassungen-btn">
                                    <Link to="/dealer/offene-zulassungen">
                                        <FileCheck2 className="w-4 h-4 mr-1.5" /> Offene Zulassungen
                                        {overdueReg > 0 && (
                                            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-black" data-testid="offene-zulassungen-overdue-badge">{overdueReg}</span>
                                        )}
                                    </Link>
                                </Button>
                            </div>
                        </div>
                        <div className="shrink-0 hidden sm:block" data-testid="dispo-header-qr">
                            <VehicleIntakeQrCard orgBranch={orgBranch} key={orgBranch} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-6 max-w-6xl">

                <RedPlateExpiryAlert className="mb-4" />

                {/* Universal Search */}
                <div className="relative bg-white border border-gray-200 rounded-sm p-4 mb-4 sticky top-2 z-10 shadow-sm">
                    {/* Jul-2026 (User-Wunsch) — Info-Dreieck (gelb, i im Kreis) ganz oben links
                        in der Ecke über „Filter"; erklärt den GESAMTEN Filterbereich. */}
                    <button
                        type="button"
                        onClick={() => setShowScopeHelp(true)}
                        title="Erklärung zum gesamten Filterbereich anzeigen"
                        aria-label="Erklärung zum Filterbereich anzeigen"
                        data-testid="disposition-scope-help-triangle"
                        className="absolute top-0 left-0 z-20 w-9 h-9"
                    >
                        <span
                            className="absolute top-0 left-0 w-0 h-0"
                            style={{ borderTop: "36px solid #FFCC00", borderRight: "36px solid transparent" }}
                        />
                        <Info className="absolute top-[3px] left-[3px] w-4 h-4 text-[#111827]" strokeWidth={3} />
                    </button>
                    {/* Jun-2026 (User-Wunsch) — Alle Filter in EINEM einklappbaren Bereich
                        zusammengefasst, damit die Liste mehr Platz bekommt. */}
                    <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="disposition-filters-header">
                        <button
                            type="button"
                            onClick={() => setShowFilters((v) => !v)}
                            aria-expanded={showFilters}
                            className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors shrink-0 ${showFilters ? "bg-orange-500 text-[#111827] border-orange-500 shadow-md font-black tw-clear-filters-attn" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                            data-testid="disposition-filters-toggle"
                        >
                            <SlidersHorizontal className="w-3.5 h-3.5" /> Filter
                            {activeFilterCount > 0 && (
                                <span className="font-mono-num bg-[#FFCC00] text-[#111827] rounded-sm px-1.5 py-0.5 text-[10px] leading-none" data-testid="disposition-filters-count">{activeFilterCount}</span>
                            )}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
                        </button>
                        {/* Jul-2026 (User-Wunsch) — Filter, Von–Bis-Filter und Universal-Suche in EINER Zeile. */}
                        <button
                            type="button"
                            onClick={() => setShowRanges((v) => !v)}
                            aria-expanded={showRanges}
                            className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors shrink-0 ${showRanges || activeRangeCount > 0 ? "bg-orange-500 text-[#111827] border-orange-500 shadow-md font-black tw-clear-filters-attn" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                            data-testid="disposition-ranges-toggle"
                        >
                            <SlidersHorizontal className="w-3.5 h-3.5" /> Von–Bis-Filter{activeRangeCount > 0 ? ` (${activeRangeCount})` : ""}
                        </button>
                        <div className="relative flex-1 min-w-[220px]">
                            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Universal-Suche: VIN · Marke · Kennzeichen · Stand-Platz · Preis · KM · Käufer · Filiale · Fahrzeug-Standort · E-Mail · Handy · Tracking-Nr.…"
                                className="pl-10 pr-10 h-11 text-sm rounded-sm border-2 border-emerald-500 focus:border-emerald-600 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                                data-testid="disposition-search-input"
                                aria-label="Universal-Suche"
                            />
                            {q && (
                                <button
                                    type="button"
                                    onClick={() => setQ("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-sm hover:bg-gray-100"
                                    aria-label="Suche leeren"
                                    data-testid="disposition-search-clear"
                                >
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            )}
                        </div>
                        {/* Jul-2026 (User-Wunsch) — Treffer-Anzeige der Universalsuche, auffällig, zwischen Suchfeld und „Alle Filter löschen". */}
                        {q.trim() !== "" && items && (
                            <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider px-3 py-2 rounded-sm bg-emerald-600 text-white shadow-md ring-2 ring-emerald-300 animate-pulse" data-testid="disposition-filter-result-count">
                                <Search className="w-3.5 h-3.5" /> {items.length} {items.length === 1 ? "Fahrzeug" : "Fahrzeuge"} gefunden
                            </span>
                        )}
                        {anyFilterActive && (
                            <button type="button" onClick={clearAllFilters}
                                className="tw-clear-filters-attn text-[11px] font-black uppercase tracking-wider px-2.5 py-1.5 rounded-sm border border-orange-400 bg-orange-500 text-white hover:bg-orange-600 inline-flex items-center gap-1 shrink-0 transition-colors"
                                data-testid="disposition-clear-all-filters-header">
                                <X className="w-3 h-3" /> Alle Filter löschen
                            </button>
                        )}
                    </div>
                    {/* Jul-2026 (User-Wunsch v2) — Farb-Schnellchips durch Eingabefeld ersetzt:
                        Bestandsfarben (mit Anzahl) erscheinen als Vorschläge beim Tippen. */}
                    {showFilters && (
                    <div data-testid="disposition-filters-body">
                    {/* Jul-2026 (User-Wunsch) — „Fahrzeuge ohne VIN löschen" in den Admin-Bereich
                        (System-Bereinigung, /admin/vehicle-duplicates) verschoben. */}
                    {/* Jun-2026 (User-Wunsch) — Übergeordneter Geltungsbereich-Filter
                        mit Erklär-Dreieck. */}
                    <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="disposition-scope-filter">
                        {[
                            { key: "my_site", label: "Mein Standort", Icon: MapPin },
                            { key: "all_sites", label: "Alle Standorte", Icon: Building2 },
                            { key: "all_dealers", label: "Alle Händler", Icon: Globe },
                        ].map(({ key, label, Icon }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setScope(key)}
                                className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                                    scope === key
                                        ? "bg-orange-500 text-[#111827] border-orange-500 shadow-md font-black tw-clear-filters-attn"
                                        : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"
                                }`}
                                data-testid={`disposition-scope-${key}`}
                            >
                                <Icon className="w-3 h-3" /> {label}
                            </button>
                        ))}
                        {/* Jul-2026 (User-Wunsch) — „NUR PRIVATE" zwischen „Alle Händler" und „Standort". Zeigt ausschließlich private (B2C) Inserate. */}
                        <button
                            type="button"
                            onClick={() => setOnlyB2c((v) => !v)}
                            aria-pressed={onlyB2c}
                            className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                                onlyB2c
                                    ? "bg-purple-600 text-white border-purple-600"
                                    : "bg-white text-purple-700 border-purple-300 hover:border-purple-600"
                            }`}
                            data-testid="disposition-b2c-filter"
                            title="Nur Inserate von Privatanbietern (B2C) anzeigen — keine Händler-Fahrzeuge"
                        >
                            <User className="w-3 h-3" /> Nur Private
                        </button>
                        {/* Jul-2026 (Variante b) — EIN gemeinsames Standort-Feld: durchsucht Händler-Filialen UND Fahrzeug-Standorte. */}
                        {(sharedDealers.length > 0 || standortOptions.length > 0) && (
                            <div className="flex items-center gap-1.5" data-testid="disposition-standort-filter-wrap">
                                <input
                                    list="dispo-standort-options"
                                    value={standortQuery}
                                    onChange={(e) => setStandortQuery(e.target.value)}
                                    placeholder="Filiale oder Fahrzeug-Standort – tippen"
                                    className={`h-8 w-72 border rounded-sm px-2 text-sm bg-white transition-colors ${standortQuery ? "border-orange-500 ring-2 ring-orange-400 font-semibold" : "border-gray-300"}`}
                                    data-testid="disposition-standort-filter"
                                    title="Nach Händler-Filiale ODER physischem Fahrzeug-Standort filtern — Eingabe möglich"
                                />
                                <datalist id="dispo-standort-options">
                                    {standortOptions.map((o) => <option key={o} value={o} />)}
                                </datalist>
                                {standortQuery && (
                                    <button type="button" onClick={() => setStandortQuery("")} className="text-gray-400 hover:text-gray-700" data-testid="disposition-standort-filter-clear"><X className="w-3.5 h-3.5" /></button>
                                )}
                                {/* Jul-2026 (User-Wunsch) — Zahnrad: ähnliche Standorte den registrierten Filial-Adressen zuordnen. */}
                                <button type="button" onClick={() => setShowLocationCleanup(true)}
                                    className="text-gray-400 hover:text-[#111827] shrink-0"
                                    title="Standorte bereinigen — ähnlich lautende Fahrzeug-Standorte den registrierten Filial-Standorten zuordnen"
                                    data-testid="disposition-standort-cleanup-gear">
                                    <Settings className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        {/* Jul-2026 (User-Wunsch) — Umkreis rechts neben Standort. */}
                        {scope === "all_dealers" && (
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="number"
                                    min="1"
                                    value={radiusKm}
                                    onChange={(e) => setRadiusKm(e.target.value)}
                                    placeholder="Umkreis km"
                                    className="h-8 w-28 border border-gray-300 rounded-sm px-2 text-sm"
                                    data-testid="disposition-radius-filter"
                                    title="Umkreis ab deiner PLZ (Händlerprofil)"
                                />
                                {radiusKm && (
                                    <button type="button" onClick={() => setRadiusKm("")} className="text-gray-400 hover:text-gray-700" data-testid="disposition-radius-clear"><X className="w-3.5 h-3.5" /></button>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Jun-2026 (User-Wunsch) — Marken-Filter für „Alle Händler" + Farbe rechts daneben (Jul-2026, User-Wunsch) */}
                    <div className="mb-3 flex items-center gap-3 flex-wrap" data-testid="disposition-b2b-filters">
                        {scope === "all_dealers" && (
                            <div className="flex items-center gap-1.5">
                                <input
                                    list="dispo-brand-options"
                                    value={brandFilter}
                                    onChange={(e) => setBrandFilter(e.target.value)}
                                    placeholder="Alle Marken"
                                    className="h-8 w-40 border border-gray-300 rounded-sm px-2 text-sm"
                                    data-testid="disposition-brand-filter"
                                />
                                <datalist id="dispo-brand-options">
                                    {brandOptions.map((b) => <option key={b} value={b} />)}
                                </datalist>
                                {brandFilter && (
                                    <button type="button" onClick={() => setBrandFilter("")} className="text-gray-400 hover:text-gray-700" data-testid="disposition-brand-clear"><X className="w-3.5 h-3.5" /></button>
                                )}
                            </div>
                        )}
                        {/* Farb-Filter: Basisfarbe, Herstellerfarbe + Farbangabe im Titel. */}
                        <div className="relative shrink-0" data-testid="disposition-color-filter-wrap">
                            <input
                                list="dispo-color-options"
                                value={colorFilter}
                                onChange={(e) => setColorFilter(e.target.value)}
                                placeholder="Farbe…"
                                className={`h-8 w-28 border rounded-sm pl-2 pr-6 text-sm transition-colors ${colorFilter.trim() ? "border-orange-500 bg-orange-50 font-bold" : "border-gray-300"}`}
                                title="Farbe filtern: Basisfarbe, Herstellerfarbe und Farbangabe in der Fahrzeugbezeichnung"
                                aria-label="Farb-Filter"
                                data-testid="disposition-color-filter"
                            />
                            <datalist id="dispo-color-options">
                                {(colorChips.length > 0
                                    ? colorChips.map(([c, n]) => ({ value: c, label: `${n} Fahrzeuge` }))
                                    : ["Schwarz", "Weiß", "Rot", "Blau", "Grün", "Gelb", "Orange", "Silber", "Grau", "Braun", "Beige", "Gold", "Matt"].map((c) => ({ value: c, label: "" }))
                                ).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </datalist>
                            {colorFilter && (
                                <button
                                    type="button"
                                    onClick={() => setColorFilter("")}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                                    aria-label="Farb-Filter leeren"
                                    data-testid="disposition-color-clear"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                    {/* Jun-2026 (User-Wunsch) — Liefertermin-Filter unten unter „Lager" angeordnet (siehe Filterbar). */}

                    {/* Phase 4f.B (Feb 2026) — Standort-Unterfilter (nur bei „Alle Standorte" & ≥2 Standorten) */}
                    {scope === "all_sites" && sites.length >= 2 && (
                        <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="disposition-site-filter">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                Standort:
                            </span>
                            <button
                                type="button"
                                onClick={() => setSiteFilter("all")}
                                className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                                    siteFilter === "all"
                                        ? "bg-orange-500 text-[#111827] border-orange-500 shadow-md font-black tw-clear-filters-attn"
                                        : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"
                                }`}
                                data-testid="disposition-site-all"
                            >
                                Alle Standorte
                            </button>
                            {sites.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setSiteFilter(s.code)}
                                    className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                                        siteFilter === s.code
                                            ? "bg-orange-500 text-[#111827] border-orange-500 shadow-md font-black tw-clear-filters-attn"
                                            : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"
                                    }`}
                                    title={s.city ? `${s.name} · ${s.city}` : s.name}
                                    data-testid={`disposition-site-${s.code}`}
                                >
                                    {s.code} · {s.name}
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Feb-2026 (User-Wunsch) — Status-Filter; Jun-2026: Geltungsbereich/Standort stehen direkt darüber (Universal-Suche-Label nach unten zum Suchfeld verschoben) */}
                    {/* Jun-2026 (User-Wunsch) — Filterbereich harmonisiert: einheitliche
                        Zeilen mit fester Label-Spalte + EINHEITLICHE „aktiv"-Kennzeichnung
                        (dunkel + gelb + Häkchen) über alle Gruppen hinweg. */}
                    <div className="mb-3 border border-gray-200 rounded-sm bg-gray-50/60 p-3 space-y-2" data-testid="disposition-filterbar">
                        {/* Status */}
                        <div className="flex items-center gap-2 flex-wrap" data-testid="disposition-status-filter">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 w-20 shrink-0">Status</span>
                            {[
                                { key: "all", label: "Alle", cnt: stats?.total },
                                { key: "verfügbar", label: "Verfügbar", cnt: stats?.available },
                                { key: "reserviert", label: "Reserviert", cnt: stats?.reserved },
                                { key: "verkauft", label: "Verkauft", cnt: stats?.sold },
                            ].map((s) => {
                                const isActive = s.key === "all"
                                    ? (statusFilters.length === 0 && !ladenhueterFilter)
                                    : (statusFilters.includes(s.key) && !ladenhueterFilter);
                                return (
                                    <button key={s.key} type="button" onClick={() => toggleStatus(s.key)} aria-pressed={isActive}
                                        className={`${isActive ? FILTER_ACTIVE : FILTER_IDLE} ${FILTER_BASE}`}
                                        data-testid={`disposition-status-${s.key}`}>
                                        {isActive && <Check className="w-3 h-3" />}{s.label}
                                        {s.cnt != null && (
                                            <span className={`font-mono-num text-[10px] leading-none px-1 py-0.5 rounded-sm ${isActive ? "bg-black/20 text-[#111827]" : "bg-gray-200 text-gray-800"}`} data-testid={`disposition-status-count-${s.key}`}>{s.cnt}</span>
                                        )}
                                    </button>
                                );
                            })}
                            {/* Jun-2026 (User-Wunsch) — Schnellfilter: Reservierung läuft bald ab (<24h) */}
                            <button type="button" onClick={applyExpiring} aria-pressed={expiringFilter}
                                className={`${expiringFilter ? FILTER_ACTIVE : FILTER_IDLE} ${FILTER_BASE}`}
                                title="Nur Reservierungen, die innerhalb der nächsten 24 Stunden ablaufen"
                                data-testid="disposition-status-expiring">
                                {expiringFilter ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}Läuft ab (&lt;24h)
                                {stats?.expiring != null && (
                                    <span className={`font-mono-num text-[10px] leading-none px-1 py-0.5 rounded-sm ${expiringFilter ? "bg-black/20 text-[#111827]" : "bg-gray-200 text-gray-800"}`} data-testid="disposition-status-count-expiring">{stats.expiring}</span>
                                )}
                            </button>
                            {/* Jul-2026 (User-Wunsch) — „Alle Filter löschen" oben rechts in der Status-Zeile */}
                            {anyFilterActive && (
                                <button type="button" onClick={clearAllFilters}
                                    className="ml-auto tw-clear-filters-attn text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-sm border border-orange-400 bg-orange-500 text-white hover:bg-orange-600 inline-flex items-center gap-1 transition-colors"
                                    data-testid="disposition-clear-all-filters">
                                    <X className="w-3 h-3" /> Alle Filter löschen
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap" data-testid="disposition-condition-filter">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 w-20 shrink-0">Zustand</span>
                            {["NW", "TZ", "VFW", "GW"].map((code) => {
                                const on = conditionFilters.includes(code);
                                const cnt = stats?.conditions?.[code];
                                return (
                                    <button key={code} type="button" onClick={() => toggleCondition(code)} aria-pressed={on}
                                        className={`${on ? FILTER_ACTIVE : FILTER_IDLE} ${FILTER_BASE}`}
                                        data-testid={`disposition-condition-${code}`}>
                                        {on && <Check className="w-3 h-3" />}{code}
                                        {cnt != null && (
                                            <span className={`font-mono-num text-[10px] leading-none px-1 py-0.5 rounded-sm ${on ? "bg-black/20 text-[#111827]" : "bg-gray-200 text-gray-800"}`} data-testid={`disposition-condition-count-${code}`}>{cnt}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {/* Lager */}
                        <div className="flex items-center gap-2 flex-wrap" data-testid="disposition-stock-filter">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 w-20 shrink-0">Lager</span>
                            {[{ key: "bestand", label: "Bestand" }, { key: "vorlauf", label: "Vorlauf" }, { key: "bestell", label: "Bestellung" }].map((s) => {
                                const on = stockFilters.includes(s.key);
                                const cnt = stats?.stock?.[s.key];
                                return (
                                    <button key={s.key} type="button" onClick={() => toggleStock(s.key)} aria-pressed={on}
                                        className={`${on ? FILTER_ACTIVE : FILTER_IDLE} ${FILTER_BASE}`}
                                        data-testid={`disposition-stock-${s.key}`}>
                                        {on && <Check className="w-3 h-3" />}{s.label}
                                        {cnt != null && (
                                            <span className={`font-mono-num text-[10px] leading-none px-1 py-0.5 rounded-sm ${on ? "bg-black/20 text-[#111827]" : "bg-gray-200 text-gray-800"}`} data-testid={`disposition-stock-count-${s.key}`}>{cnt}</span>
                                        )}
                                    </button>
                                );
                            })}
                            {/* Hersteller-Katalog-Liste aufrufen (Jun 2026 · User-Wunsch) */}
                            <button type="button" onClick={() => setShowCatalog(true)}
                                className={`${FILTER_IDLE} ${FILTER_BASE}`}
                                title="Bestellcodes aus Hersteller-Katalog ansehen"
                                data-testid="disposition-manufacturer-btn">
                                <Factory className="w-3 h-3" /> Hersteller
                            </button>
                        </div>
                        {/* Liefertermin (Jun-2026: unter „Lager" angeordnet, im Reset berücksichtigt) */}
                        <div className="flex items-center gap-2 flex-wrap" data-testid="disposition-delivery-filters">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 w-20 shrink-0">Liefertermin</span>
                            <button type="button" onClick={() => setDeliveryFilter((v) => !v)} aria-pressed={deliveryFilter}
                                className={`${deliveryFilter ? FILTER_ACTIVE : FILTER_IDLE} ${FILTER_BASE}`}
                                data-testid="disposition-delivery-filter">
                                {deliveryFilter && <Check className="w-3 h-3" />}Nur mit Liefertermin
                                {stats?.delivery != null && (
                                    <span className={`font-mono-num text-[10px] leading-none px-1 py-0.5 rounded-sm ${deliveryFilter ? "bg-black/20 text-[#111827]" : "bg-gray-200 text-gray-800"}`} data-testid="disposition-delivery-count">{stats.delivery}</span>
                                )}
                            </button>
                            <button type="button" onClick={() => setDeliverySort((v) => !v)} aria-pressed={deliverySort}
                                className={`${deliverySort ? FILTER_ACTIVE : FILTER_IDLE} ${FILTER_BASE}`}
                                data-testid="disposition-delivery-sort">
                                {deliverySort && <Check className="w-3 h-3" />}Nach Liefertermin sortieren
                            </button>
                            {/* Jul-2026 (User-Wunsch) — Gruppenbearbeitung + „Alle" rechts bündig in der Liefertermin-Zeile */}
                            <div className="ml-auto flex items-center gap-2" data-testid="disposition-bulk-controls">
                                {canBulkEdit && (
                                <Button type="button" onClick={() => setShowBulk(true)} disabled={selectedIds.size === 0}
                                    className="h-8 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-[11px] font-bold uppercase tracking-wider disabled:opacity-40"
                                    data-testid="disposition-bulk-edit-btn">
                                    <Layers className="w-3.5 h-3.5 mr-1" />
                                    Gruppenbearbeitung{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                                </Button>
                                )}
                                {canBulkEdit && items && items.length > 0 && (
                                    <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-600 cursor-pointer" data-testid="disposition-select-all-label">
                                        <input type="checkbox" className="w-4 h-4 accent-[#FFCC00]"
                                            checked={selectedIds.size > 0 && items.every((it) => selectedIds.has(it.id))}
                                            ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && !items.every((it) => selectedIds.has(it.id)); }}
                                            onChange={(e) => { if (e.target.checked) setSelectedIds(new Set(items.map((it) => it.id))); else clearSelection(); }}
                                            data-testid="disposition-select-all" />
                                        Alle
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>
                    </div>
                    )}
                    {/* Von–Bis-Toggle wurde in die obere Filter-Zeile verschoben (Jul-2026). */}
                    {showRanges && (
                        <div className="mb-3 pb-3 border-b border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="disposition-ranges-panel">
                            {[
                                { label: "Erstzulassung / Baujahr", from: "min_year", to: "max_year", ph1: "ab Jahr", ph2: "bis Jahr" },
                                { label: "Kilometerstand", from: "min_mileage", to: "max_mileage", ph1: "ab km", ph2: "bis km" },
                                { label: "Verkaufspreis (€)", from: "min_price", to: "max_price", ph1: "ab €", ph2: "bis €" },
                            ].map((grp) => (
                                <div key={grp.label}>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">{grp.label}</label>
                                    <div className="flex items-center gap-1.5">
                                        <Input
                                            type="number" min="0" inputMode="numeric"
                                            value={ranges[grp.from]}
                                            onChange={(e) => setRange(grp.from, e.target.value)}
                                            placeholder={grp.ph1}
                                            className="h-9 rounded-sm font-mono-num text-sm"
                                            data-testid={`disposition-range-${grp.from}`}
                                        />
                                        <span className="text-gray-400 text-xs shrink-0">–</span>
                                        <Input
                                            type="number" min="0" inputMode="numeric"
                                            value={ranges[grp.to]}
                                            onChange={(e) => setRange(grp.to, e.target.value)}
                                            placeholder={grp.ph2}
                                            className="h-9 rounded-sm font-mono-num text-sm"
                                            data-testid={`disposition-range-${grp.to}`}
                                        />
                                    </div>
                                </div>
                            ))}
                            {activeRangeCount > 0 && (
                                <div className="sm:col-span-3">
                                    <button
                                        type="button"
                                        onClick={clearRanges}
                                        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                                        data-testid="disposition-ranges-clear"
                                    >
                                        <X className="w-3.5 h-3.5" /> Bereichsfilter zurücksetzen
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Universal-Suche wurde in die obere Filter-Zeile verschoben (Jul-2026). */}
                    <div className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-2">
                        {loading && <><Loader2 className="w-3 h-3 animate-spin" /> Suche läuft …</>}
                        {!loading && ladenhueterFilter && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 border border-red-300 text-red-700 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="disposition-active-ladenhueter">
                                <AlertTriangle className="w-2.5 h-2.5" /> Filter: Ladenhüter &gt;120T
                            </span>
                        )}
                    </div>
                </div>

                {/* Feb-2026 (User-Wunsch) — Kennzahl-Leiste: Lagerzustand auf einen Blick.
                    Juni-2026 (User-Wunsch) — Kacheln sind anklickbar und filtern die Liste
                    (Verfügbar/Reserviert/Verkauft/Gesamt → Status, Ladenhüter >120T → Standtage). */}
                {stats && (
                    <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="disposition-stats-bar">
                        {[
                            { key: "available", label: "Verfügbar", value: stats.available, cls: "text-emerald-600", onClick: () => applyStatus("verfügbar"), active: statusFilters.length === 1 && statusFilters[0] === "verfügbar" && !ladenhueterFilter, ring: "ring-emerald-400 bg-emerald-50 border-emerald-300" },
                            { key: "reserved", label: "Reserviert", value: stats.reserved, cls: "text-amber-600", onClick: () => applyStatus("reserviert"), active: statusFilters.length === 1 && statusFilters[0] === "reserviert" && !ladenhueterFilter, ring: "ring-amber-400 bg-amber-50 border-amber-300" },
                            { key: "sold", label: "Verkauft", value: stats.sold, cls: "text-red-600", onClick: () => applyStatus("verkauft"), active: statusFilters.length === 1 && statusFilters[0] === "verkauft" && !ladenhueterFilter, ring: "ring-red-400 bg-red-50 border-red-300" },
                            { key: "total", label: "Gesamt", value: stats.total, cls: "text-[#111827]", onClick: () => applyStatus("all"), active: statusFilters.length === 0 && !ladenhueterFilter, ring: "ring-[#111827] bg-gray-50 border-gray-400" },
                            { key: "avg", label: "Ø Standtage", value: `${stats.avg_standtage}`, cls: stats.avg_standtage > 120 ? "text-red-600" : stats.avg_standtage > 90 ? "text-amber-600" : "text-[#111827]", onClick: null, active: false, ring: "" },
                            { key: "ladenhueter", label: "Ladenhüter >120T", value: stats.ladenhueter, cls: stats.ladenhueter > 0 ? "text-red-600" : "text-[#111827]", onClick: applyLadenhueter, active: ladenhueterFilter, ring: "ring-red-400 bg-red-50 border-red-300" },
                        ].map((s) => {
                            const clickable = !!s.onClick;
                            const stateCls = s.active
                                ? `ring-2 ${s.ring}`
                                : clickable
                                    ? "bg-white border-gray-200 hover:border-[#111827] hover:shadow-sm cursor-pointer"
                                    : "bg-white border-gray-200";
                            const Comp = clickable ? "button" : "div";
                            return (
                                <Comp
                                    key={s.key}
                                    type={clickable ? "button" : undefined}
                                    onClick={s.onClick || undefined}
                                    className={`rounded-sm px-3 py-2 text-left border transition-all ${stateCls}`}
                                    data-testid={`disposition-stat-${s.key}`}
                                    title={clickable ? `Nach „${s.label}" filtern` : undefined}
                                    aria-pressed={clickable ? s.active : undefined}
                                >
                                    <div className={`text-xl font-black font-mono-num leading-none ${s.cls}`}>{s.value}</div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-1 truncate">{s.label}</div>
                                </Comp>
                            );
                        })}
                    </div>
                )}

                {/* Results */}
                {items === null && (
                    <div className="text-center py-12 text-gray-500" data-testid="disposition-loading">
                        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                    </div>
                )}
                {items && items.length === 0 && (
                    <div className="text-center py-12 text-gray-500 bg-white border border-gray-200 rounded-sm" data-testid="disposition-empty">
                        {scope === "my_site" && noSite ? (
                            <>
                                <MapPin className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                                <div className="font-bold text-gray-700">Kein Standort-Code hinterlegt</div>
                                <div className="text-sm mt-1">Für „Mein Standort" muss in deinem Händlerprofil ein Standort-Code gepflegt sein. Wechsle auf „Alle Standorte" oder hinterlege einen Standort-Code in den Einstellungen.</div>
                            </>
                        ) : (
                            "Keine Fahrzeuge gefunden — versuchen Sie einen anderen Suchbegriff."
                        )}
                    </div>
                )}
                {items && items.length > 0 && scope === "all_dealers" && items.some((i) => i.foreign) && (
                    <div className="mb-3 flex items-start gap-2 text-[12px] text-blue-800 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="disposition-foreign-banner">
                        <Globe className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
                        <span>Blau markierte Einträge sind Fahrzeuge <strong>anderer Händler</strong> (B2B-freigegeben). Sie sind nur zur Ansicht — Kauf, Reservierung und Vermittlung erfolgen über die Detailseite des Fahrzeugs.</span>
                    </div>
                )}
                {items && items.length > 0 && (
                    <>
                    <ul className="space-y-2" data-testid="disposition-results">
                        {pagedItems.map((it) => (
                            <DispositionVehicleCard
                                key={`${it.is_foreign ? "foreign" : "own"}-${it.id}`}
                                item={it}
                                currentStandort={currentStandort}
                                selected={selectedIds.has(it.id)}
                                onToggleSelect={toggleSelect}
                                onLadenhueterAction={setLhAction}
                                onCreateOffer={setOfferItem}
                                onTestDrive={setTestDriveItem}
                                onForeignReserve={setForeignReserveItem}
                                onForeignDeal={setForeignDealItem}
                                onCreateDelivery={setDeliveryItem}
                                onCreateTransport={setTransportItem}
                                onSofortmitnahme={setSofortmitnahmeItem}
                            />
                        ))}
                    </ul>
                    {/* Jun-2026 (User-Wunsch) — Paginierung 10/20/50. */}
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap" data-testid="disposition-pagination">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="font-bold uppercase tracking-wider text-[10px] text-gray-500">Pro Seite:</span>
                            {[10, 20, 50].map((sz) => (
                                <button key={sz} type="button" onClick={() => { setPageSize(sz); setPage(1); }}
                                    className={`px-2.5 py-1 rounded-sm border text-[11px] font-bold ${pageSize === sz ? "bg-orange-500 text-[#111827] border-orange-500 shadow-md font-black tw-clear-filters-attn" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                                    data-testid={`disposition-page-size-${sz}`}>{sz}</button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span data-testid="disposition-page-info">{(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, totalItems)} von {totalItems}</span>
                            <Button type="button" size="sm" variant="outline" disabled={curPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="h-8" data-testid="disposition-page-prev">Zurück</Button>
                            <Button type="button" size="sm" variant="outline" disabled={curPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="h-8" data-testid="disposition-page-next">Weiter</Button>
                        </div>
                    </div>
                    </>
                )}
            </div>
            {lhAction && (
                <LadenhueterActionModal
                    item={lhAction}
                    onClose={() => setLhAction(null)}
                    onDone={() => { setLhAction(null); load(); }}
                />
            )}
            {offerItem && (
                <OfferSendModal item={offerItem} onClose={() => setOfferItem(null)} />
            )}
            {testDriveItem && (
                <TestDriveModal item={testDriveItem} onClose={() => setTestDriveItem(null)} />
            )}
            {deliveryItem && (
                <DeliveryOrderModal item={deliveryItem} onClose={() => setDeliveryItem(null)} />
            )}
            {transportItem && (
                <TransportOrderModal item={transportItem} onClose={() => setTransportItem(null)} />
            )}
            {foreignReserveItem && (
                <B2BForeignActionModal
                    item={foreignReserveItem}
                    kind="reserve"
                    onClose={() => setForeignReserveItem(null)}
                    onDone={() => { setForeignReserveItem(null); load(); }}
                />
            )}
            {foreignDealItem && (
                <B2BForeignActionModal
                    item={foreignDealItem}
                    kind="deal"
                    onClose={() => setForeignDealItem(null)}
                    onDone={() => { setForeignDealItem(null); load(); }}
                />
            )}
            {/* Jun-2026 (User-Wunsch) — Erklär-Popup zum Geltungsbereich (Info-Dreieck). */}
            <Dialog open={showCatalog} onOpenChange={setShowCatalog}>
                <DialogContent className="max-w-lg" data-testid="disposition-manufacturer-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Factory className="w-5 h-5 text-amber-600" /> Hersteller-Bestellcodes
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-gray-500 -mt-2">
                        Bestellcodes aus dem importierten Hersteller-Katalog durchsuchen. Klick auf einen Eintrag filtert die Disposition nach diesem Bestellcode.
                    </p>
                    <CatalogOrderCodePicker withCounts onPick={(it) => {
                        setShowCatalog(false);
                        setQ(it.order_code);
                        toast.success(`Disposition gefiltert nach Bestellcode ${it.order_code} · ${it.model}`);
                    }} />
                </DialogContent>
            </Dialog>

            <Dialog open={showScopeHelp} onOpenChange={setShowScopeHelp}>
                <DialogContent className="max-w-lg" data-testid="disposition-scope-help-popup">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Info className="w-5 h-5 text-[#FFCC00]" />
                            {DISPO_GUIDES.dispoScope?.title || "Geltungsbereich"}
                        </DialogTitle>
                    </DialogHeader>
                    <ul className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
                        {(DISPO_GUIDES.dispoScope?.features || []).map((f, i) => (
                            <li key={i} className="flex gap-2.5" data-testid={`disposition-scope-help-item-${i}`}>
                                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FFCC00] shrink-0" />
                                <div>
                                    <div className="text-sm font-bold text-gray-900">{f.name}</div>
                                    <div className="text-[12px] text-gray-600 leading-snug">{f.desc}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
            </Dialog>
            {showBulk && (
                <DispoBulkEditModal
                    count={selectedIds.size}
                    listingIds={Array.from(selectedIds)}
                    sites={sites}
                    onClose={() => setShowBulk(false)}
                    onDone={() => { setShowBulk(false); clearSelection(); load(); }}
                />
            )}
            {showHistory && (
                <DispoBulkHistoryModal
                    onClose={() => setShowHistory(false)}
                    onUndone={() => load()}
                />
            )}
            {showPapersBatch && (
                <PapersBatchScanModal
                    onClose={() => setShowPapersBatch(false)}
                    onDone={() => load()}
                />
            )}
            <HsnTsnLookupModal open={showHsnTsn} onOpenChange={setShowHsnTsn} />
            <LocationCleanupModal open={showLocationCleanup} onOpenChange={setShowLocationCleanup} onDone={() => load()} />
            <ListingDupesModal open={showListingDupes} onOpenChange={setShowListingDupes} onDone={() => load()} />
            {dispoSettingsOpen && <DispoSettingsModal onClose={() => setDispoSettingsOpen(false)} />}
        </div>
    );
}
